import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { getWrestlerName, getWrestlerNameUpper } from './wrestlerUtils';
import { getPLELogo } from './plesDatabase';

const S = {
  anton: { fontFamily: "'Anton', sans-serif" },
  mono: { fontFamily: "'Monda', sans-serif" },
};

const fmt = (v) => Math.round(Number(v || 0)).toLocaleString('fr-FR').replace(/,/g, ' ');
const fmtBudget = (v) => '$' + fmt(v);

const getWrestlerRender = (w) => {
  if (!w) return null;
  if (w.customRender) return w.customRender;
  if (w.wrestler_id) return process.env.PUBLIC_URL + '/wrestlers/' + w.wrestler_id + '-render.png';
  return null;
};

// ── Compute season stats ─────────────────────────────────────────────
function computeSeasonStats(gameData) {
  var seasonHistory = (gameData && gameData.seasonHistory) || [];
  var fans = (gameData && gameData.fans) || 0;
  var fanStart = seasonHistory.length > 0 ? (seasonHistory[0].fans || 0) : fans;
  var fanGrowth = fans - fanStart;
  var peakFans = seasonHistory.length > 0 ? Math.max(...seasonHistory.map(w => w.fans || 0)) : fans;
  var allRatings = seasonHistory.map(w => w.avgRating || 0).filter(r => r > 0);
  var bestRating = allRatings.length > 0 ? Math.max(...allRatings).toFixed(1) : '—';
  var avgRating = allRatings.length > 0 ? (allRatings.reduce((s, r) => s + r, 0) / allRatings.length).toFixed(1) : '—';
  var pleWeeks = seasonHistory.filter(w => w.weekType === 'ple' || w.weekType === 'wrestlemania');
  var roster = (gameData && gameData.roster) || [];
  var pleWins = pleWeeks.filter(w => {
    var matches = w.matches || [];
    var myWins = matches.filter(m => m.winner && roster.find(r => r.name === m.winner) && !m.noContest);
    var total = matches.filter(m => !m.noContest).length;
    return total > 0 && myWins.length > total / 2;
  }).length;
  var pleLosses = pleWeeks.length - pleWins;
  var titleChanges = (gameData && gameData.titleHistory || []).length;
  var totalRevenue = seasonHistory.reduce((s, w) => s + (w.profits || 0), 0);
  var budget = (gameData && gameData.budget) || 0;
  var AUTO_FAN_THRESHOLDS = [2300000, 3400000, 4500000, 5250000, 6000000];
  var AUTO_PROFIT_THRESHOLDS = [2000000, 5250000, 8750000, 12500000, 16000000];
  var hofActive = (gameData && gameData.hofActive) || [];
  var autoFan = AUTO_FAN_THRESHOLDS.filter(t => fans >= t).length;
  var autoProfit = AUTO_PROFIT_THRESHOLDS.filter(t => totalRevenue >= t).length;
  var hofCount = hofActive.length + autoFan + autoProfit;
  return { fans, fanGrowth, peakFans, bestRating, avgRating, pleRecord: pleWins + 'W — ' + pleLosses + 'L', titleChanges, totalRevenue, budget, hofCount };
}

// ── Compute 3 random roster highlights ──────────────────────────────
function computeRosterHighlights(gameData, allWrestlers) {
  var seasonHistory = (gameData && gameData.seasonHistory) || [];
  var roster = (gameData && gameData.roster) || [];
  var titleHistory = (gameData && gameData.titleHistory) || [];
  var pastRivalries = (gameData && gameData.pastRivalries) || [];
  var fullPool = allWrestlers && allWrestlers.length > 0 ? allWrestlers : roster;

  var wrestlerStats = {};
  seasonHistory.forEach(week => {
    (week.matches || []).forEach(m => {
      var allP = [];
      if (m.participants && m.participants.length > 0) {
        m.participants.filter(Boolean).forEach(p => allP.push(typeof p === 'string' ? p : (p.name || getWrestlerName(p, 'display') || '')));
      } else if (m.teams) {
        m.teams.forEach(t => { var members = Array.isArray(t) ? t : (t.members || []); members.filter(Boolean).forEach(p => allP.push(typeof p === 'string' ? p : (p.name || ''))); });
      }
      allP.forEach(name => {
        if (!name || name === '?') return;
        if (!wrestlerStats[name]) wrestlerStats[name] = { bookings: 0, wins: 0, losses: 0 };
        wrestlerStats[name].bookings++;
        var winnerName = typeof m.winner === 'object' ? (m.winner && (m.winner.name || getWrestlerName(m.winner, 'display'))) : m.winner;
        if (winnerName === name) wrestlerStats[name].wins++;
        else if (winnerName && winnerName !== name && !m.noContest) wrestlerStats[name].losses++;
      });
    });
  });

  var getRosterW = (name) => {
    if (!name) return null;
    var n = name.trim().toLowerCase();
    return fullPool.find(w => {
      if (!w) return false;
      if (w.name && w.name.toLowerCase() === n) return true;
      if ((getWrestlerName(w, 'display') || '').toLowerCase() === n) return true;
      if ((getWrestlerName(w, 'full') || '').toLowerCase() === n) return true;
      return false;
    }) || null;
  };

  var mostBooked = Object.entries(wrestlerStats).sort((a, b) => b[1].bookings - a[1].bookings)[0];
  var mostWins = Object.entries(wrestlerStats).sort((a, b) => b[1].wins - a[1].wins)[0];
  var bestRecord = Object.entries(wrestlerStats).filter(([, s]) => s.bookings >= 4).sort((a, b) => (b[1].wins / Math.max(b[1].bookings, 1)) - (a[1].wins / Math.max(a[1].bookings, 1)))[0];
  var mvp = Object.entries(wrestlerStats).map(([name, s]) => {
    var rivScore = pastRivalries.filter(r => (r.members || []).some(m => (m.name || m) === name) && r.winner === name).length;
    return { name, score: s.wins * 2 + s.bookings + rivScore * 3 };
  }).sort((a, b) => b.score - a.score)[0];
  var longestReign = null; var longestReignWeeks = 0;
  titleHistory.forEach(e => {
    var dur = (e.endWeek || 0) - (e.startWeek || 0);
    if (dur > longestReignWeeks && e.champions && e.champions.length > 0) { longestReignWeeks = dur; longestReign = e; }
  });

  var pool = [
    mostBooked && { label: 'MOST BOOKED', wrestler: mostBooked[0], wrestlerObj: getRosterW(mostBooked[0]), main: mostBooked[1].bookings + ' MATCHS', sub: mostBooked[1].wins + 'W / ' + mostBooked[1].losses + 'L' },
    mostWins && { label: 'MOST WINS', wrestler: mostWins[0], wrestlerObj: getRosterW(mostWins[0]), main: mostWins[1].wins + ' WINS', sub: mostWins[1].bookings + ' MATCHS' },
    bestRecord && { label: 'BEST W/L RECORD', wrestler: bestRecord[0], wrestlerObj: getRosterW(bestRecord[0]), main: bestRecord[1].wins + 'W / ' + bestRecord[1].losses + 'L', sub: bestRecord[1].bookings + ' MATCHS' },
    mvp && { label: 'MVP OF THE SEASON', wrestler: mvp.name, wrestlerObj: getRosterW(mvp.name), main: wrestlerStats[mvp.name] ? wrestlerStats[mvp.name].wins + ' WINS' : '—', sub: wrestlerStats[mvp.name] ? wrestlerStats[mvp.name].bookings + ' BOOKINGS' : '—' },
    longestReign && { label: 'LONGEST REIGN', wrestler: longestReign.champions[0], wrestlerObj: getRosterW(longestReign.champions[0]), main: longestReignWeeks + ' WEEKS', sub: longestReign.title || 'CHAMPIONSHIP' },
  ].filter(Boolean);

  var seed = ((gameData && gameData.season) || 1) * 137 + 42;
  var arr = [...pool]; var picked = [];
  while (picked.length < 3 && arr.length > 0) {
    seed = (seed * 1664525 + 1013904223) & 0xffffffff;
    var idx = Math.abs(seed) % arr.length;
    picked.push(arr.splice(idx, 1)[0]);
  }
  return picked;
}

// ── Wrestler mini card ───────────────────────────────────────────────
const WrestlerCard = ({ wrestlerObj, name, main, sub, label }) => {
  var render = wrestlerObj ? getWrestlerRender(wrestlerObj) : null;
  var displayName = wrestlerObj ? getWrestlerNameUpper(wrestlerObj) : (name || '').toUpperCase();
  return (
    <div style={{ background: 'rgba(0,0,0,0.55)', border: '1px solid rgba(212,175,55,0.12)', borderRadius: '7px', overflow: 'hidden', position: 'relative', flex: 1 }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '1px', background: 'linear-gradient(90deg, transparent, rgba(212,175,55,0.3), transparent)' }} />
      <div style={{ ...S.mono, fontSize: '9px', letterSpacing: '2px', color: 'rgba(212,175,55,0.65)', padding: '7px 10px 5px', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px' }}>
        <div style={{ width: '54px', height: '70px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '4px', overflow: 'hidden', position: 'relative', flexShrink: 0 }}>
          {render
            ? <img loading="lazy" src={render} alt="" style={{ position: 'absolute', bottom: '-12px', left: '50%', transform: 'translateX(-50%)', height: '90px', objectFit: 'contain', objectPosition: 'bottom' }} onError={e => { e.target.style.display = 'none'; }} />
            : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', color: 'rgba(255,255,255,0.15)' }}>?</div>
          }
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ ...S.mono, fontSize: '9px', color: 'rgba(255,255,255,0.4)', marginBottom: '3px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayName}</div>
          <div style={{ ...S.anton, fontSize: '18px', color: '#fff', letterSpacing: '1px', lineHeight: 1.1 }}>{main}</div>
          <div style={{ ...S.mono, fontSize: '10px', color: 'rgba(255,255,255,0.3)', marginTop: '3px' }}>{sub}</div>
        </div>
      </div>
    </div>
  );
};

// ── MAIN ─────────────────────────────────────────────────────────────
export default function SeasonRecap({ gameData, onContinue }) {
  var brand = (gameData && gameData.brand) || '';
  var brandColor = (gameData && gameData.brandColor) || '#e01010';
  var brandLogo = (gameData && gameData.brandLogo) ? gameData.brandLogo : (process.env.PUBLIC_URL + '/brands/logos/' + brand.toUpperCase().replace(/ /g, '') + '_LOGO.png');
  var season = (gameData && gameData.season) || 1;
  var myRank = (gameData && gameData._myRank) || 1;
  var rankSuffix = myRank === 1 ? 'ST' : myRank === 2 ? 'ND' : myRank === 3 ? 'RD' : 'TH';

  var [allWrestlers, setAllWrestlers] = useState([]);
  useEffect(() => {
    var gameVersion = (gameData && gameData.gameSettingsData && gameData.gameSettingsData.gameVersion) || '2k26';
    var collName = gameVersion === '2k25' ? 'wrestlers_2k25' : 'wrestlers_2k26';
    getDocs(collection(db, collName)).then(snap => setAllWrestlers(snap.docs.map(d => d.data()))).catch(() => {});
  }, []);

  var stats = computeSeasonStats(gameData);
  var highlights = computeRosterHighlights(gameData, allWrestlers);

  var css = `
    .sr-root * { box-sizing: border-box; }
    @keyframes srFadeIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes scaleReveal { from { opacity: 0; transform: scale(0.88); } to { opacity: 1; transform: scale(1); } }
    .sr-fade { animation: srFadeIn 0.5s ease forwards; }
    .sr-scale { animation: scaleReveal 0.3s cubic-bezier(0.34,1.56,0.64,1) both; }
    .sr-btn:hover { filter: brightness(1.15); transform: translateY(-1px); }
  `;

  return (
    <div className="sr-root" style={{ position: 'fixed', inset: 0, zIndex: 998, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.88)' }}>
      <style>{css}</style>

      <div className="sr-fade" style={{ width: '100%', maxWidth: '720px', background: '#080500', border: '1px solid rgba(212,175,55,0.2)', borderRadius: '12px', overflow: 'hidden', margin: '20px' }}>
        {/* Gold line top */}
        <div style={{ height: '2px', background: 'linear-gradient(90deg, transparent, #d4af37, transparent)' }} />

        {/* Header */}
        <div style={{ padding: '16px 22px 12px', borderBottom: '1px solid rgba(212,175,55,0.1)', display: 'flex', alignItems: 'center', gap: '14px' }}>
          {/* Brand logo */}
          <div style={{ width: '60px', height: '52px', background: brandColor + '18', border: '1px solid ' + brandColor + '44', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden', padding: '4px' }}>
            <img loading="lazy" src={brandLogo} alt={brand} style={{ maxWidth: '56px', maxHeight: '44px', objectFit: 'contain' }} onError={e => { e.target.style.display = 'none'; }} />
          </div>
          <div>
            <div style={{ ...S.mono, fontSize: '10px', letterSpacing: '3px', color: 'rgba(212,175,55,0.5)', marginBottom: '1px' }}>END OF SEASON {season}</div>
            <div style={{ ...S.anton, fontSize: '20px', letterSpacing: '3px', color: '#fff', lineHeight: 1 }}>SEASON RECAP</div>
            {(gameData && gameData.gm) && <div style={{ ...S.mono, fontSize: '10px', color: 'rgba(255,255,255,0.35)', letterSpacing: '1px', marginTop: '3px' }}>{(gameData.gm).toUpperCase()} · {brand.toUpperCase()} GENERAL MANAGER</div>}
          </div>
          <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
            <div style={{ ...S.mono, fontSize: '10px', color: 'rgba(255,255,255,0.3)', letterSpacing: '1px' }}>YOUR RANK</div>
            <div style={{ ...S.anton, fontSize: '22px', color: myRank === 1 ? '#d4af37' : myRank === 2 ? '#95a5a6' : myRank === 3 ? '#e67e22' : 'rgba(255,255,255,0.5)', letterSpacing: '2px' }}>{myRank}{rankSuffix} PLACE</div>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: '16px 22px', display: 'flex', gap: '16px' }}>

          {/* LEFT — Stats */}
          <div style={{ width: '240px', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <div style={{ ...S.mono, fontSize: '9px', letterSpacing: '2px', color: 'rgba(212,175,55,0.45)', marginBottom: '2px' }}>BRAND STATS</div>
            <div className="sr-scale" style={{ background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(212,175,55,0.1)', borderRadius: '7px', padding: '10px 12px', position: 'relative', animationDelay: '0ms' }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '1px', background: 'linear-gradient(90deg,transparent,rgba(212,175,55,0.3),transparent)' }} />

              {/* Fan stats */}
              <StatRow label="Total Fans" value={fmt(stats.fans)} color="#3498db" />
              <StatRow label="Fan Growth" value={(stats.fanGrowth >= 0 ? '+' : '') + fmt(stats.fanGrowth)} color="#3498db" />
              <StatRow label="Peak Fans" value={fmt(stats.peakFans)} color="#3498db" />

              {/* Separator */}
              <div style={{ height: '1px', background: 'rgba(255,255,255,0.04)', margin: '4px 0' }} />

              {/* Show stats */}
              <StatRow label="Best Show Rating" value={stats.bestRating + ' ★'} color="#f39c12" />
              <StatRow label="Avg Show Rating" value={stats.avgRating + ' ★'} color="#f39c12" />
              <StatRow label="PLE Record" value={stats.pleRecord} color="#9b59b6" />
              <StatRow label="Title Changes" value={stats.titleChanges} />

              {/* Separator */}
              <div style={{ height: '1px', background: 'rgba(255,255,255,0.04)', margin: '4px 0' }} />

              {/* Money + HOF */}
              <StatRow label="Total Revenue" value={fmtBudget(stats.totalRevenue)} color="#2ecc71" />
              <StatRow label="Budget" value={fmtBudget(stats.budget)} color="#2ecc71" />
              <StatRow label="HOF Trophies" value={stats.hofCount} color="#d4af37" noBorder />
            </div>
          </div>

          {/* RIGHT — Roster highlights */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ ...S.mono, fontSize: '9px', letterSpacing: '2px', color: 'rgba(212,175,55,0.45)', marginBottom: '2px' }}>ROSTER HIGHLIGHTS</div>
            {highlights.map((h, i) => (
              <div key={i} className="sr-scale" style={{ animationDelay: (80 + i * 100) + 'ms' }}>
                <WrestlerCard {...h} />
              </div>
            ))}
            {highlights.length === 0 && (
              <div style={{ ...S.mono, fontSize: '11px', color: 'rgba(255,255,255,0.2)', padding: '20px 0' }}>No match data available yet.</div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 22px', borderTop: '1px solid rgba(212,175,55,0.1)' }}>
          <button className="sr-btn" onClick={onContinue}
            style={{ width: '100%', ...S.anton, fontSize: '13px', letterSpacing: '3px', background: 'rgba(212,175,55,0.1)', border: '1px solid rgba(212,175,55,0.5)', color: '#d4af37', padding: '12px', borderRadius: '6px', cursor: 'pointer', transition: 'filter 0.2s, transform 0.2s' }}>
            SELECT MY SUPERSTARS →
          </button>
        </div>

        {/* Gold line bottom */}
        <div style={{ height: '2px', background: 'linear-gradient(90deg, transparent, #d4af37, transparent)' }} />
      </div>
    </div>
  );
}

const StatRow = ({ label, value, color, noBorder }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '4px 0', borderBottom: noBorder ? 'none' : '1px solid rgba(255,255,255,0.04)' }}>
    <span style={{ fontFamily: "'Monda', sans-serif", fontSize: '11px', color: 'rgba(255,255,255,0.38)' }}>{label}</span>
    <span style={{ fontFamily: "'Anton', sans-serif", fontSize: '13px', color: color || '#d4af37', letterSpacing: '0.5px' }}>{value}</span>
  </div>
);
